import React, { useState, useEffect, useContext } from 'react';
import { Container, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import api from '../services/api';

const ProfilePage = () => {
    const { user, business, updateAuthTokens } = useContext(AuthContext);
    const { t, i18n } = useTranslation();
    const [profileData, setProfileData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        language: user?.language || '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                email: user.email || '',
                language: user.language || '',
            });
        }
    }, [user]);

    const handleChange = (e) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const toastId = toast.loading(t('profilePage.toasts.updating'));

        try {
            const response = await api.put(`/users/${user.id}/profile`, profileData);
            const { user: updatedUser, token } = response.data;
            
            const existingAuthTokens = JSON.parse(localStorage.getItem('authTokens'));
            const newAuthTokens = {
                token,
                refreshToken: existingAuthTokens.refreshToken,
            };

            updateAuthTokens(newAuthTokens);
            
            // Immediately change the language in the app
            const newLang = profileData.language || business?.language || 'en';
            i18n.changeLanguage(newLang);

            toast.success(t('profilePage.toasts.updated'), { id: toastId });
        } catch (err) {
            console.error("Failed to update profile", err);
            const errorMessage = err.response?.data?.message || t('profilePage.toasts.updateFailed');
            setError(errorMessage);
            toast.error(errorMessage, { id: toastId });
        } finally {
            setSubmitting(false);
        }
    };
    
    // ... el resto del código del componente sigue aquí ...
    
    if (!user || loading) {
        return <Container className="mt-4 text-center"><Spinner animation="border" /></Container>;
    }

    if (error) {
        return <Container className="mt-4"><Alert variant="danger">{error}</Alert></Container>;
    }

    return (
        <Container className="mt-4">
            <Card>
                <Card.Header as="h2">{t('profilePage.title')}</Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>{t('profilePage.nameLabel')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={profileData.name}
                                onChange={handleChange}
                                placeholder={t('profilePage.namePlaceholder')}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="email">
                            <Form.Label>{t('profilePage.emailLabel')}</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={profileData.email}
                                onChange={handleChange}
                                placeholder={t('profilePage.emailPlaceholder')}
                                disabled // Email is usually not editable directly
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="language">
                            <Form.Label>{t('profilePage.language.label')}</Form.Label>
                            <Form.Select name="language" value={profileData.language || ''} onChange={handleChange}>
                                <option value="">{t('profilePage.language.default')}</option>
                                <option value="en">{t('profilePage.language.english')}</option>
                                <option value="es">{t('profilePage.language.spanish')}</option>
                            </Form.Select>
                        </Form.Group>

                        <Button variant="primary" type="submit" className="mt-3" disabled={submitting}>
                            {submitting ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> {t('profilePage.savingButton')}</> : t('profilePage.saveButton')}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ProfilePage;
